import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Share2, Copy, Trash2, Plus, Link as LinkIcon, Users2, Loader2 } from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

/**
 * Share dialog: creates private view-only scoring links for the audition team
 * (director, MD, choreographer, etc.). Anyone with a link can see all scores
 * and comments, but cannot score, comment, or cast.
 */
export default function ShareScoresDialog({ productionId, open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audition-score-shares", { params: { production_id: productionId } });
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load share links");
    } finally { setLoading(false); }
  }, [productionId]);

  useEffect(() => { if (open && productionId) load(); }, [open, productionId, load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/audition-score-shares", { label }, { params: { production_id: productionId } });
      setItems((it) => [data, ...it]);
      setLabel("");
      copyLink(data.token);
      toast.success("Share link created and copied");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const revoke = async (share) => {
    if (!confirm(`Revoke "${share.label}"? Anyone using this link will lose access.`)) return;
    try {
      await api.delete(`/audition-score-shares/${share.id}`);
      setItems((it) => it.filter((s) => s.id !== share.id));
      toast.success("Link revoked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  };

  const buildUrl = (token) => `${window.location.origin}/audition-scores/${token}`;

  const copyLink = (token) => {
    navigator.clipboard?.writeText(buildUrl(token));
    toast.success("Link copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
            <Share2 className="w-5 h-5 text-gold" /> Share audition scores
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-3">
            <div className="flex items-start gap-2.5">
              <Users2 className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <div className="text-xs text-zinc-400 leading-relaxed">
                Private, view-only links for your audition team (director, MD, choreographer).
                Everyone with a link sees <span className="text-zinc-200">all scores and comments</span> from
                every scorer — but cannot score, comment, or assign roles. Only Admins can cast.
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-3">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Create new link</Label>
            <div className="mt-2 flex gap-2">
              <Input
                data-testid="share-link-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Callback team · Aug 3"
                className="bg-[#121212] border-white/10 h-10 flex-1"
              />
              <button
                data-testid="share-link-create"
                onClick={create}
                disabled={busy}
                style={goldBtn}
                className="h-10 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-50 whitespace-nowrap"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
              </button>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2">Active links</div>
            {loading && <div className="text-xs text-zinc-500 py-4 text-center">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="text-xs text-zinc-500 py-6 text-center rounded-lg border border-dashed border-white/10">
                No share links yet. Create one above to send to your audition team.
              </div>
            )}
            {items.map((s) => (
              <div key={s.id} className="mt-2 rounded-lg border border-white/10 bg-[#121212] p-3 group">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-100 truncate">{s.label}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      Created by {s.created_by_name || "someone"} · {fmtDate(s.created_at)}
                    </div>
                  </div>
                  <button
                    data-testid={`share-link-copy-${s.id}`}
                    onClick={() => copyLink(s.token)}
                    className="h-8 px-2.5 rounded border border-white/10 text-xs text-zinc-200 hover:bg-white/5 inline-flex items-center gap-1.5"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                  <button
                    data-testid={`share-link-revoke-${s.id}`}
                    onClick={() => revoke(s)}
                    className="h-8 w-8 grid place-items-center rounded border border-red-500/30 text-red-400 hover:bg-red-500/10"
                    title="Revoke link"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="mt-2 text-[11px] font-mono text-zinc-500 break-all inline-flex items-center gap-1.5">
                  <LinkIcon className="w-3 h-3 shrink-0" /> {buildUrl(s.token)}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm">Done</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
